import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/layout/ProtectedRoute';
import Layout from './components/layout/Layout';
import SessionTimeoutModal from './components/common/SessionTimeoutModal';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ForceChangePassword from './pages/ForceChangePassword';
import ChangePassword from './pages/account/ChangePassword';

// Admin
import UserManagement from './pages/admin/UserManagement';

// HRMS
import EmployeeList from './pages/hrms/EmployeeList';
import EmployeeForm from './pages/hrms/EmployeeForm';
import EmployeeDetail from './pages/hrms/EmployeeDetail';
import Departments from './pages/hrms/Departments';
import LeaveManagement from './pages/hrms/LeaveManagement';
import WFHManagement from './pages/hrms/WFHManagement';
import ClientVisitManagement from './pages/hrms/ClientVisitManagement';
import AttendanceManagement from './pages/hrms/AttendanceManagement';
import AttendanceApproval from './pages/hrms/AttendanceApproval';
import TeamAttendanceApproval from './pages/hrms/TeamAttendanceApproval';
import TimesheetAdmin from './pages/hrms/TimesheetAdmin';
import Performance from './pages/hrms/Performance';
import Announcements from './pages/hrms/Announcements';
import HallOfFame from './pages/hrms/HallOfFame';
import Training from './pages/hrms/Training';
import Payroll from './pages/hrms/Payroll';
import Interviews from './pages/hrms/Interviews';
import OrgChart from './pages/hrms/OrgChart';
import PayFixation from './pages/hrms/PayFixation';
import Promotions from './pages/hrms/Promotions';
import Transfers from './pages/hrms/Transfers';

// IT
import AssetList from './pages/it/AssetList';
import AssetForm from './pages/it/AssetForm';
import AssetDetail from './pages/it/AssetDetail';
import AssetCategories from './pages/it/AssetCategories';
import Licenses from './pages/it/Licenses';
import TicketList from './pages/it/TicketList';
import TicketForm from './pages/it/TicketForm';
import TicketDetail from './pages/it/TicketDetail';
import MyQueue from './pages/it/MyQueue';

// Finance
import FinanceDashboard from './pages/finance/FinanceDashboard';
import FinanceClaims from './pages/finance/FinanceClaims';
import MonthlyReport from './pages/finance/MonthlyReport';

// Employee
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import MyLeave from './pages/employee/MyLeave';
import MyWFH from './pages/employee/MyWFHSingleDate';
import MyAttendance from './pages/employee/MyAttendance';
import MyAssets from './pages/employee/MyAssets';
import MyTickets from './pages/employee/MyTickets';
import MyClaims from './pages/employee/MyClaims';
import ClaimForm from './pages/employee/ClaimForm';
import MyPayslips from './pages/employee/MyPayslips';
import MyOrgChart from './pages/employee/MyOrgChart';
import MyPerformance from './pages/employee/MyPerformance';
import CompleteProfile from './pages/employee/CompleteProfile';
import MyProfile from './pages/employee/MyProfile';
import CEODashboard from './pages/ceo/CEODashboard';
import ProjectList from './pages/projects/ProjectList';
import ProjectDetail from './pages/projects/ProjectDetail';
import MyTasks from './pages/projects/MyTasks';
import TestManagement from './pages/projects/TestManagement';
import EPFForm11 from './pages/employee/EPFForm11';
import ESIForm1 from './pages/employee/ESIForm1';
import MyTimesheet from './pages/employee/MyTimesheet';
import MyTraining from './pages/employee/MyTraining';
import MyClientVisit from './pages/employee/MyClientVisit';

// HRMS Admin
import WFHLimitSetup from './pages/hrms/WFHLimitSetup';

// Sales
import SalesDashboard from './pages/sales/SalesDashboard';
import TenderList from './pages/sales/TenderList';
import TenderDetail from './pages/sales/TenderDetail';
import Vendors from './pages/sales/Vendors';

// Office
import VisitorManagement from './pages/office/VisitorManagement';

// Attendance
import Attendance from './pages/hrms/Attendance';
import ProfileVerification from './pages/hrms/ProfileVerification';

// ─── Role constants (include legacy aliases for backward compatibility) ────────
const ALL_ROLES = [
  'super_admin', 'ceo', 'cfo', 'cto',
  'hr_head', 'hr_executive', 'hr_admin',
  'it_admin', 'it_support',
  'finance_executive', 'finance_team', 'finance_head',
  'project_manager', 'project_lead', 'team_lead', 'reporting_manager',
  'sales_head', 'sales_executive', 'office_admin',
  'employee',
];
const SALES_ROLES = ['sales_head', 'sales_executive', 'cto', 'cfo', 'ceo', 'super_admin'];
const OFFICE_ROLES = ['office_admin', 'super_admin', 'hr_admin', 'hr_head'];
const CEO_ROLES = ['ceo', 'cfo', 'cto', 'super_admin'];
const HR_ROLES = ['hr_head', 'hr_executive', 'hr_admin', 'super_admin'];
const HR_FULL_ROLES = ['hr_head', 'hr_admin', 'super_admin'];
const IT_ROLES = ['it_admin', 'super_admin'];
const IT_ALL_ROLES = ['it_admin', 'it_support', 'super_admin'];
const FINANCE_ROLES = ['finance_executive', 'cfo', 'finance_team', 'finance_head', 'super_admin'];
const FINANCE_FULL_ROLES = ['cfo', 'finance_head', 'super_admin'];
const EMPLOYEE_ROLES = [
  'super_admin', 'ceo', 'cfo', 'cto',
  'hr_head', 'hr_executive', 'hr_admin',
  'it_admin', 'it_support',
  'finance_executive', 'finance_team', 'finance_head',
  'project_manager', 'project_lead', 'team_lead', 'reporting_manager',
  'solution_architect', 'dev_lead', 'developer', 'qa_lead', 'tester', 'business_analyst',
  'sales_head', 'sales_executive', 'office_admin',
  'employee',
];
// Roles that see all projects
const PM_ROLES = ALL_ROLES;
// Roles that can see employee list (view at minimum)
const EMP_VIEW_ROLES = [
  'hr_head', 'hr_executive', 'hr_admin', 'super_admin',
  'ceo', 'cfo', 'cto', 'finance_head', 'it_admin',
  'project_manager', 'project_lead', 'team_lead', 'reporting_manager',
];

// function RootRedirect() {
//   const { isAuthenticated, isLoading, user } = useAuth();
//   if (isLoading) return null;
//   if (!isAuthenticated) return <Navigate to="/login" replace />;
//   if (user?.must_change_password) return <Navigate to="/change-password" replace />;
//   return <Navigate to="/dashboard" replace />;
// }

function RootRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.must_change_password) return <Navigate to="/change-password" replace />;
  return <Navigate to="/employee/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/change-password" element={<ForceChangePassword />} />
      <Route path="/account/change-password" element={
        <ProtectedRoute allowedRoles={ALL_ROLES}>
          <Layout><ChangePassword /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/" element={<RootRedirect />} />

      {/* Protected routes wrapped in Layout */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={ALL_ROLES}>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Admin */}
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <Layout><UserManagement /></Layout>
        </ProtectedRoute>
      } />

      {/* HRMS - Employees */}
      <Route path="/hrms/employees" element={
        <ProtectedRoute allowedRoles={EMP_VIEW_ROLES}>
          <Layout><EmployeeList /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/employees/new" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'cfo']}>
          <Layout><EmployeeForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/employees/:id" element={
        <ProtectedRoute allowedRoles={EMP_VIEW_ROLES}>
          <Layout><EmployeeDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/employees/:id/edit" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'cfo']}>
          <Layout><EmployeeForm /></Layout>
        </ProtectedRoute>
      } />

      {/* HRMS - Other */}
      <Route path="/hrms/departments" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'ceo', 'cto', 'cfo']}>
          <Layout><Departments /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/leave" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'project_manager', 'project_lead', 'team_lead', 'reporting_manager', 'cto', 'cfo', 'ceo']}>
          <Layout><LeaveManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/wfh" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'cto', 'cfo', 'ceo']}>
          <Layout><WFHManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/client-visits" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'cto', 'cfo', 'ceo']}>
          <Layout><ClientVisitManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/wfh-limits" element={
        <ProtectedRoute allowedRoles={HR_ROLES}>
          <Layout><WFHLimitSetup /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/profile-verification" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'cfo']}>
          <Layout><ProfileVerification /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/attendance" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'cfo', 'ceo', 'cto']}>
          <Layout><AttendanceManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/attendance-approval" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'cfo']}>
          <Layout><AttendanceApproval /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/team-attendance-approval" element={
        <ProtectedRoute allowedRoles={ALL_ROLES}>
          <Layout><TeamAttendanceApproval /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/timesheet-admin" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'ceo', 'cfo', 'cto']}>
          <Layout><TimesheetAdmin /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/performance" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'cto', 'ceo']}>
          <Layout><Performance /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/announcements" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'ceo', 'cto']}>
          <Layout><Announcements /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/hall-of-fame" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'ceo', 'cto']}>
          <Layout><HallOfFame /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/training" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'ceo', 'cto']}>
          <Layout><Training /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/payroll" element={
        <ProtectedRoute allowedRoles={[...HR_FULL_ROLES, 'cfo', 'finance_head']}>
          <Layout><Payroll /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/interviews" element={
        <ProtectedRoute allowedRoles={HR_ROLES}>
          <Layout><Interviews /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/org-chart" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'ceo', 'cfo', 'cto']}>
          <Layout><OrgChart /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/pay-fixation" element={
        <ProtectedRoute allowedRoles={[...HR_FULL_ROLES, 'cfo', 'finance_head']}>
          <Layout><PayFixation /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/promotions" element={
        <ProtectedRoute allowedRoles={HR_FULL_ROLES}>
          <Layout><Promotions /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms/transfers" element={
        <ProtectedRoute allowedRoles={HR_ROLES}>
          <Layout><Transfers /></Layout>
        </ProtectedRoute>
      } />

      {/* IT */}
      <Route path="/it/assets" element={
        <ProtectedRoute allowedRoles={[...IT_ALL_ROLES, 'cfo', 'cto', 'finance_head']}>
          <Layout><AssetList /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/assets/new" element={
        <ProtectedRoute allowedRoles={[...IT_ROLES, 'cfo', 'cto']}>
          <Layout><AssetForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/assets/:id" element={
        <ProtectedRoute allowedRoles={[...IT_ALL_ROLES, 'cfo', 'cto', 'finance_head']}>
          <Layout><AssetDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/assets/:id/edit" element={
        <ProtectedRoute allowedRoles={[...IT_ROLES, 'cfo', 'cto']}>
          <Layout><AssetForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/categories" element={
        <ProtectedRoute allowedRoles={[...IT_ALL_ROLES, 'cfo', 'cto']}>
          <Layout><AssetCategories /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/licenses" element={
        <ProtectedRoute allowedRoles={[...IT_ALL_ROLES, 'cfo', 'cto']}>
          <Layout><Licenses /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/tickets" element={
        <ProtectedRoute allowedRoles={[...IT_ALL_ROLES, 'cfo', 'cto']}>
          <Layout><TicketList isAdmin={true} /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/tickets/new" element={
        <ProtectedRoute allowedRoles={IT_ROLES}>
          <Layout><TicketForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/tickets/:id" element={
        <ProtectedRoute allowedRoles={[...IT_ALL_ROLES, ...EMPLOYEE_ROLES]}>
          <Layout><TicketDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/it/my-queue" element={
        <ProtectedRoute allowedRoles={IT_ALL_ROLES}>
          <Layout><MyQueue /></Layout>
        </ProtectedRoute>
      } />

      {/* Finance */}
      <Route path="/finance/dashboard" element={
        <ProtectedRoute allowedRoles={[...FINANCE_ROLES, 'ceo']}>
          <Layout><FinanceDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/finance/claims" element={
        <ProtectedRoute allowedRoles={[...FINANCE_ROLES, 'ceo']}>
          <Layout><FinanceClaims /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/finance/reports" element={
        <ProtectedRoute allowedRoles={[...FINANCE_FULL_ROLES, 'ceo']}>
          <Layout><MonthlyReport /></Layout>
        </ProtectedRoute>
      } />

      {/* Employee self-service */}
      <Route path="/employee/dashboard" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><EmployeeDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/leave" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyLeave /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/wfh" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyWFH /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/attendance" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyAttendance /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/assets" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyAssets /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/tickets" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyTickets /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/tickets/new" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><TicketForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/claims" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyClaims /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/claims/new" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><ClaimForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/claims/:id/edit" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><ClaimForm /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/employee/payslips" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyPayslips /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/org-chart" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyOrgChart /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/performance" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyPerformance /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/employee/my-profile" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyProfile /></Layout>
        </ProtectedRoute>
      } />

      {/* Employee self-onboarding */}
      <Route path="/employee/complete-profile" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><CompleteProfile /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/epf-form" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><EPFForm11 /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/esi-form" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><ESIForm1 /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/timesheet" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyTimesheet /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/training" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyTraining /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employee/client-visit" element={
        <ProtectedRoute allowedRoles={EMPLOYEE_ROLES}>
          <Layout><MyClientVisit /></Layout>
        </ProtectedRoute>
      } />

      {/* Project Management */}
      <Route path="/projects" element={
        <ProtectedRoute allowedRoles={PM_ROLES}>
          <Layout><ProjectList /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/projects/:projectId" element={
        <ProtectedRoute allowedRoles={PM_ROLES}>
          <Layout><ProjectDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-tasks" element={
        <ProtectedRoute allowedRoles={ALL_ROLES}>
          <Layout><MyTasks /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/test-management" element={
        <ProtectedRoute allowedRoles={ALL_ROLES}>
          <Layout><TestManagement /></Layout>
        </ProtectedRoute>
      } />

      {/* Sales */}
      <Route path="/sales/dashboard" element={
        <ProtectedRoute allowedRoles={SALES_ROLES}>
          <Layout><SalesDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/sales/tenders" element={
        <ProtectedRoute allowedRoles={SALES_ROLES}>
          <Layout><TenderList /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/sales/tenders/:id" element={
        <ProtectedRoute allowedRoles={SALES_ROLES}>
          <Layout><TenderDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/sales/vendors" element={
        <ProtectedRoute allowedRoles={SALES_ROLES}>
          <Layout><Vendors /></Layout>
        </ProtectedRoute>
      } />

      {/* Office / VMS */}
      <Route path="/office/visitors" element={
        <ProtectedRoute allowedRoles={OFFICE_ROLES}>
          <Layout><VisitorManagement /></Layout>
        </ProtectedRoute>
      } />

      {/* Biometric Attendance */}
      <Route path="/hrms/biometric-attendance" element={
        <ProtectedRoute allowedRoles={[...HR_ROLES, 'ceo', 'cfo', 'cto', 'super_admin']}>
          <Layout><Attendance /></Layout>
        </ProtectedRoute>
      } />

      {/* CEO */}
      <Route path="/ceo/dashboard" element={
        <ProtectedRoute allowedRoles={CEO_ROLES}>
          <Layout><CEODashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <SessionTimeoutModal />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
